import { degToRad, detCos, detSin, PI } from './detmath';

/** Anything holding a serialisable 32-bit PRNG state (MatchState, bot brains...). */
export interface RngHolder {
  rng: number;
}

/** mulberry32: small, fast, fully specified by 32-bit integer operations. */
export function nextFloat(holder: RngHolder): number {
  holder.rng = (holder.rng + 0x6d2b79f5) >>> 0;
  let t = holder.rng;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

export function nextInt(holder: RngHolder, exclusiveMax: number): number {
  return Math.floor(nextFloat(holder) * exclusiveMax);
}

export function nextRange(holder: RngHolder, min: number, max: number): number {
  return min + (max - min) * nextFloat(holder);
}

/** Uniform unit vector whose angle stays at least `minAxisDeg` away from both axes. */
export function nextDiagonalDirection(
  holder: RngHolder,
  minAxisDeg: number,
): { x: number; y: number } {
  const quadrant = nextInt(holder, 4);
  const margin = degToRad(minAxisDeg);
  const angle = quadrant * (PI / 2) + margin + nextFloat(holder) * (PI / 2 - 2 * margin);
  return { x: detCos(angle), y: detSin(angle) };
}

/** Converts any seed (number or string) into a 32-bit PRNG state. */
export function seedToState(seed: number | string): number {
  const text = typeof seed === 'number' ? `n:${seed}` : `s:${seed}`;
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}
